import { useState, useEffect, useMemo } from "react";
import Modal from "../ui/Modal";
import Input from "../ui/Input";
import Select from "../ui/Select";
import Button from "../ui/Button";
import OfferAllocationEditor from "../ui/OfferAllocationEditor";
import { toISO, today, monthKeyFromDate } from "../../lib/dateUtils";
import { formatNumber } from "../../lib/formatters";

const blank = () => ({
  name: "",
  role: "",
  email: "",
  phone: "",
  instapayHandle: "",
  department: "",
  monthlySalary: 0,
  startDate: toISO(today()),
  endDate: "",
  status: "active",
  isOwner: false,
  notes: "",
  offerAllocations: [],
});

// Find the most recent FX rate at or before `monthKey`. If none, fall
// back to the latest rate available. Returns null if no rates exist.
function pickRate(fxRates, monthKey) {
  if (!fxRates || fxRates.length === 0) return null;
  const sorted = [...fxRates].sort((a, b) => a.month.localeCompare(b.month));
  // Try exact match
  const exact = sorted.find((r) => r.month === monthKey);
  if (exact) return exact;
  // Most recent before or equal to monthKey
  const prior = [...sorted].reverse().find((r) => r.month <= monthKey);
  if (prior) return prior;
  // Earliest available
  return sorted[0];
}

export default function TeamForm({ open, onClose, onSubmit, initial, offers = [], fxRates = [], departments = [] }) {
  const [form, setForm] = useState(() => initial || blank());
  const [errors, setErrors] = useState({});
  // Salary input is decoupled from the canonical monthlySalary so the user
  // can type in EGP and we still store USD. Two pieces of form-only state:
  //   inputCurrency: "USD" | "EGP" — which unit the input is in
  //   salaryInput:   the typed value in that unit (string, for empty-state
  //                  control + decimals while typing)
  const [inputCurrency, setInputCurrency] = useState("USD");
  const [salaryInput, setSalaryInput] = useState("");

  // Pick the rate for the current month (or the most recent prior one).
  // Salary entry is "now", so today's month is the right anchor.
  const rate = useMemo(() => {
    return pickRate(fxRates, monthKeyFromDate(today()))?.usdToEgp || null;
  }, [fxRates]);

  // When the form opens, seed the salary input. If the member has a
  // nativeCurrency/nativeSalary saved, restore those (so editing an EGP
  // salary opens in EGP at the typed amount). Otherwise fall back to
  // monthlySalary (always USD).
  useEffect(() => {
    if (!open) return;
    const initialForm = initial || blank();
    setForm(initialForm);
    setErrors({});
    if (initialForm.nativeCurrency && initialForm.nativeSalary) {
      setInputCurrency(initialForm.nativeCurrency);
      setSalaryInput(String(initialForm.nativeSalary));
    } else {
      setInputCurrency("USD");
      setSalaryInput(initialForm.monthlySalary ? String(initialForm.monthlySalary) : "");
    }
  }, [open, initial]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Compute the canonical USD salary based on inputCurrency.
  const usdFromInput = useMemo(() => {
    const v = Number(salaryInput);
    if (!Number.isFinite(v) || v <= 0) return 0;
    if (inputCurrency === "EGP" && rate && rate > 0) return v / rate;
    return v;
  }, [salaryInput, inputCurrency, rate]);

  // When user types a value, also store it as monthlySalary (USD).
  const handleSalaryChange = (e) => {
    const raw = e.target.value;
    setSalaryInput(raw);
    const v = Number(raw);
    if (!Number.isFinite(v) || v <= 0) {
      update("monthlySalary", 0);
      return;
    }
    const usd = inputCurrency === "EGP" && rate && rate > 0 ? v / rate : v;
    update("monthlySalary", usd);
  };

  // Switch currency without losing the user's intent. If they had typed in
  // USD and switch to EGP, convert the displayed amount; same the other way.
  const toggleCurrency = (next) => {
    if (next === inputCurrency) return;
    if (rate && rate > 0) {
      const v = Number(salaryInput);
      if (Number.isFinite(v) && v > 0) {
        const converted = next === "EGP" ? v * rate : v / rate;
        setSalaryInput(String(Math.round(converted * 100) / 100));
      }
    }
    setInputCurrency(next);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const errs = {};
    if (!form.name.trim()) errs.name = "Required";
    if (!form.role.trim()) errs.role = "Required";
    if (!form.startDate) errs.startDate = "Required";
    if (inputCurrency === "EGP" && (!rate || rate <= 0)) {
      errs.salary = "Set this month's USD→EGP rate in Settings before saving an EGP salary.";
    }
    if (Object.keys(errs).length) { setErrors(errs); return; }
    const typedAmount = Number(salaryInput) || 0;
    onSubmit({
      ...form,
      // Round to whole dollars so we don't carry long EGP-conversion
      // decimals into the matrix display. This stays as a fallback for
      // months with no FX rate set.
      monthlySalary: Math.round(usdFromInput),
      // Native amount + currency drives month-by-month USD conversion in
      // the salary matrix when set to EGP.
      nativeSalary: typedAmount,
      nativeCurrency: inputCurrency,
      endDate: form.endDate || null,
      phone: form.phone || null,
      instapayHandle: form.instapayHandle || null,
      department: form.department || null,
      isOwner: form.isOwner === true,
      offerAllocations: (form.offerAllocations || [])
        .filter((a) => {
          const scope = a.scope || (a.offerId ? "offer" : null);
          if (scope === "offer") return !!a.offerId;
          if (scope === "department") return !!a.scopeId;
          if (scope === "agency") return true;
          return false;
        })
        .map((a) => ({ ...a, value: Number(a.value) || 0 })),
    });
    onClose();
  };

  // Conversion preview shown under the salary input.
  let conversionHint = null;
  if (salaryInput && Number(salaryInput) > 0) {
    if (inputCurrency === "EGP") {
      if (rate && rate > 0) {
        conversionHint = (
          <span className="text-emerald-700 dark:text-emerald-300">
            ≈ ${formatNumber(usdFromInput, 2)} USD at {formatNumber(rate, 2)} EGP/USD
          </span>
        );
      } else {
        conversionHint = (
          <span className="text-red-600">No FX rate set — go to Settings → Currency exchange.</span>
        );
      }
    } else if (rate && rate > 0) {
      conversionHint = (
        <span className="text-brand-slate">
          ≈ {formatNumber(Number(salaryInput) * rate, 0)} EGP at {formatNumber(rate, 2)} EGP/USD
        </span>
      );
    }
  }

  return (
    <Modal open={open} onClose={onClose} title={initial ? "Edit team member" : "Add team member"}>
      <form onSubmit={handleSubmit} className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <Input label="Name *" value={form.name} onChange={(e) => update("name", e.target.value)} error={errors.name} />
          <Input label="Role *" value={form.role} onChange={(e) => update("role", e.target.value)} error={errors.role} placeholder="e.g. Sales, Ops" />
        </div>
        <div className="grid grid-cols-3 gap-3">
          <Input label="Email" type="email" value={form.email} onChange={(e) => update("email", e.target.value)} />
          <Input label="Phone" type="tel" value={form.phone || ""} onChange={(e) => update("phone", e.target.value)} placeholder="+20 1XX XXX XXXX" />
          <Input label="Instapay handle" value={form.instapayHandle || ""} onChange={(e) => update("instapayHandle", e.target.value)} placeholder="@handle" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs font-medium text-brand-slate">Monthly salary</span>
              <div className="inline-flex items-center bg-gray-100 dark:bg-[var(--bg-soft)] rounded-md p-0.5 text-[10px] font-semibold">
                <button
                  type="button"
                  onClick={() => toggleCurrency("USD")}
                  className={`px-2 py-0.5 rounded ${inputCurrency === "USD" ? "bg-white dark:bg-[var(--bg-surface)] text-brand-ink dark:text-[var(--text-primary)] shadow-sm" : "text-brand-slate"}`}
                >
                  USD
                </button>
                <button
                  type="button"
                  onClick={() => toggleCurrency("EGP")}
                  className={`px-2 py-0.5 rounded ${inputCurrency === "EGP" ? "bg-white dark:bg-[var(--bg-surface)] text-brand-ink dark:text-[var(--text-primary)] shadow-sm" : "text-brand-slate"}`}
                >
                  EGP
                </button>
              </div>
            </div>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-brand-slate text-sm pointer-events-none">
                {inputCurrency === "USD" ? "$" : "E£"}
              </span>
              <input
                type="number"
                step="0.01"
                min="0"
                value={salaryInput}
                onChange={handleSalaryChange}
                placeholder="0"
                className={`w-full pl-8 pr-3 py-2 border rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-brand-blue/30 dark:bg-[var(--bg-soft)] dark:border-[var(--border-soft)] dark:text-[var(--text-primary)] ${errors.salary ? "border-red-400" : "border-gray-200"}`}
              />
            </div>
            <div className="mt-1 min-h-[1rem] text-[11px]">
              {errors.salary ? <span className="text-red-600">{errors.salary}</span> : conversionHint}
            </div>
            {initial && !initial.nativeCurrency && (
              <div className="mt-2 rounded-md border border-amber-200 bg-amber-50 dark:bg-amber-900/10 dark:border-amber-800/40 px-2.5 py-1.5 text-[11px] text-amber-800 dark:text-amber-200">
                This salary was added before currency tracking. Pick USD or EGP and save to enable month-by-month FX conversion in the salary matrix.
              </div>
            )}
          </div>
          <div>
            <Select
              label="Department"
              value={form.department || ""}
              onChange={(e) => update("department", e.target.value)}
            >
              <option value="">— Agency level —</option>
              {departments.map((d) => <option key={d} value={d}>{d}</option>)}
            </Select>
            <p className="text-[11px] text-brand-slate mt-1">
              {form.department
                ? `Salary attributed to ${form.department}.`
                : "Leave empty to keep this as agency overhead."}
            </p>
          </div>
        </div>
        <div className="grid grid-cols-3 gap-3">
          <Input label="Start date *" type="date" value={form.startDate} onChange={(e) => update("startDate", e.target.value)} error={errors.startDate} />
          <Input label="End date" type="date" value={form.endDate || ""} onChange={(e) => update("endDate", e.target.value)} />
          <Select label="Status" value={form.status} onChange={(e) => update("status", e.target.value)}>
            <option value="active">Active</option>
            <option value="left">Left</option>
            <option value="paused">Paused</option>
          </Select>
        </div>

        {/* Owner flag — used to track owner draws (withdrawals vs. salary owed). */}
        {/* Only one owner is expected (Ahmed). UI doesn't enforce — soft convention. */}
        <label className="flex items-start gap-2.5 rounded-md border border-gray-200 dark:border-[var(--border-soft)] px-3 py-2 cursor-pointer hover:bg-gray-50 dark:hover:bg-[var(--bg-soft)]">
          <input
            type="checkbox"
            checked={form.isOwner === true}
            onChange={(e) => update("isOwner", e.target.checked)}
            className="mt-0.5 cursor-pointer"
          />
          <span>
            <span className="block text-sm font-medium text-brand-ink dark:text-[var(--text-primary)]">This is the owner (Ahmed)</span>
            <span className="block text-[11px] text-brand-slate">
              Owner salary = "what should have been paid me each month". Withdrawals matched as "Owner withdrawal" are compared to this owed amount on the Overview tab.
            </span>
          </span>
        </label>

        <div className="border-t border-gray-100 dark:border-[var(--border-soft)] pt-3">
          <OfferAllocationEditor
            value={form.offerAllocations || []}
            onChange={(next) => update("offerAllocations", next)}
            offers={offers}
            departments={departments}
          />
        </div>

        <label className="block">
          <span className="block text-xs font-medium text-brand-slate mb-1">Notes</span>
          <textarea
            rows={2}
            value={form.notes}
            onChange={(e) => update("notes", e.target.value)}
            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-brand-blue/30 dark:bg-[var(--bg-soft)] dark:border-[var(--border-soft)] dark:text-[var(--text-primary)]"
          />
        </label>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="secondary" type="button" onClick={onClose}>Cancel</Button>
          <Button type="submit">{initial ? "Save" : "Add member"}</Button>
        </div>
      </form>
    </Modal>
  );
}
